import React, { useState, useEffect } from "react";
import FastAPIClient from "../../../client";
import config from "../../../config";
import { DataGridPremium, GridColDef } from "@mui/x-data-grid-premium";
import { frFR } from "@mui/x-data-grid/locales";
import { Box, CircularProgress, Chip, Stack } from "@mui/material";
import { FiX } from "react-icons/fi";

const client = new FastAPIClient(config);

interface ClientProductCategory {
    client_id: string;
    client_name: string;
    sales_rep_name: string;
    warehouse_name: string;
    city: string;
    [key: string]: string | number;
}

interface User {
    user_id: string;
    warehouse_id: string;
    user_role_id: string;
    [key: string]: string;
}

const RevenueTable: React.FC = () => {
    const [data, setData] = useState<ClientProductCategory[]>([]);
    const [columns, setColumns] = useState<string[]>([]);
    const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
    const [user, setUser] = useState<User | null>(null);
    const [gridColumns, setGridColumns] = useState<GridColDef[]>([]);
    const [loading, setLoading] = useState(true);
    const [columnFilters, setColumnFilters] = useState<
        Record<string, "all" | "sold" | "notSold">
    >({});

    const getUserName = () => {
        client.fetchUser().then((data: User) => {
            setUser(data);
        });
    };

    useEffect(() => {
        getUserName();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            if (!user?.user_id) return;
            setLoading(true);
            try {
                const data = await client.getClientProductCategories();

                console.log("Fetched data:", data.results);
                setData(data.results);

                if (data.results?.length > 0) {
                    const initialColumns = Object.keys(data.results[0]).filter(
                        (key) =>
                            ![
                                "client_id",
                                "client_name",
                                "city",
                                "sales_rep_name",
                                "warehouse_name",
                            ].includes(key)
                    );
                    setColumns(initialColumns);

                    // Select "BIERES" by default if it exists in the initial columns
                    if (initialColumns.includes("BIERES")) {
                        setSelectedColumns((prev) => {
                            if (!prev.includes("BIERES")) {
                                return [...prev, "BIERES"];
                            }
                            return prev;
                        });
                        setColumnFilters((prev) => ({
                            ...prev,
                            BIERES: "all",
                        }));
                    }
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [user]);

    useEffect(() => {
        const baseColumns: GridColDef[] = [
            {
                field: "warehouse_name",
                headerName: "Dépôt",
                width: 150,
            },
            {
                field: "sales_rep_name",
                headerName: "Commercial",
                width: 150,
            },
            {
                field: "city",
                headerName: "Ville",
                width: 150,
                renderCell: (params) => {
                    if (!params.value) return "";
                    const formattedValue = params.value
                        .replace(/"/g, "")
                        .toLowerCase()
                        .split(" ")
                        .map(
                            (word) =>
                                word.charAt(0).toUpperCase() + word.slice(1)
                        )
                        .join(" ");
                    return <div>{formattedValue}</div>;
                },
            },
            {
                field: "client_name",
                headerName: "Nom client",
                width: 200,
                renderCell: (params) => {
                    if (!params.value) return "";
                    const formattedValue = params.value
                        .replace(/"/g, "")
                        .toLowerCase()
                        .split(" ")
                        .map(
                            (word) =>
                                word.charAt(0).toUpperCase() + word.slice(1)
                        )
                        .join(" ");
                    return <div>{formattedValue}</div>;
                },
            },
            {
                field: "client_id",
                headerName: "ID client",
                width: 120,
            },
            ...selectedColumns.map((col) => ({
                field: col,
                headerName: col,
                width: 200,
                type: "number" as const,
                sortable: false,
                renderCell: (params) => {
                    const value = params.value;
                    const numericValue = Number(value);

                    if (!isNaN(numericValue)) {
                        // It's a number (including 0 or negative, though likely > 0 based on context)
                        if (numericValue > 0) {
                            // Round positive numbers to 0 decimal places and append ' €'
                            return `${numericValue.toFixed(0)} €`;
                        } else if (numericValue === 0) {
                            // Display 0 as '0 €'
                            return "0 €";
                        } else {
                            // Handle negative numbers if necessary, for now just show with €
                            return `${numericValue.toFixed(0)} €`;
                        }
                    } else {
                        // Not a number, display original string with ' €'
                        return value + " €";
                    }
                },
                renderHeader: () => (
                    <Box>
                        <div>
                            {col
                                .toLowerCase()
                                .split(" ")
                                .map(
                                    (word) =>
                                        word.charAt(0).toUpperCase() +
                                        word.slice(1)
                                )
                                .join(" ")}
                        </div>
                        <Stack direction="row" spacing={0.5}>
                            <Chip
                                size="small"
                                label="Tous"
                                onClick={() =>
                                    setColumnFilters((prev) => ({
                                        ...prev,
                                        [col]: "all",
                                    }))
                                }
                                color={
                                    columnFilters[col] === "all"
                                        ? "primary"
                                        : "default"
                                }
                                variant={
                                    columnFilters[col] === "all"
                                        ? "filled"
                                        : "outlined"
                                }
                            />
                            <Chip
                                size="small"
                                label="Vendu"
                                onClick={() =>
                                    setColumnFilters((prev) => ({
                                        ...prev,
                                        [col]: "sold",
                                    }))
                                }
                                color={
                                    columnFilters[col] === "sold"
                                        ? "primary"
                                        : "default"
                                }
                                variant={
                                    columnFilters[col] === "sold"
                                        ? "filled"
                                        : "outlined"
                                }
                            />
                            <Chip
                                size="small"
                                label="Pas vendu"
                                onClick={() =>
                                    setColumnFilters((prev) => ({
                                        ...prev,
                                        [col]: "notSold",
                                    }))
                                }
                                color={
                                    columnFilters[col] === "notSold"
                                        ? "primary"
                                        : "default"
                                }
                                variant={
                                    columnFilters[col] === "notSold"
                                        ? "filled"
                                        : "outlined"
                                }
                            />
                        </Stack>
                    </Box>
                ),
            })),
        ];

        setGridColumns(baseColumns);
    }, [selectedColumns, columnFilters]);

    if (loading) {
        return (
            <Box
                sx={{
                    height: 600,
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ height: 600, width: "100%" }}>
            <Stack
                direction="row"
                spacing={1}
                sx={{ mb: 1, flexWrap: "wrap", gap: 1 }}
            >
                {columns.map((col) => (
                    <Chip
                        key={col}
                        label={col
                            .toLowerCase()
                            .split(" ")
                            .map(
                                (word) =>
                                    word.charAt(0).toUpperCase() + word.slice(1)
                            )
                            .join(" ")}
                        onClick={() => {
                            if (!selectedColumns.includes(col)) {
                                setSelectedColumns((prev) => [...prev, col]);
                                setColumnFilters((prev) => ({
                                    ...prev,
                                    [col]: "all",
                                }));
                            }
                        }}
                        onDelete={
                            selectedColumns.includes(col)
                                ? () => {
                                      setSelectedColumns((prev) =>
                                          prev.filter((c) => c !== col)
                                      );
                                      setColumnFilters((prev) => {
                                          const newFilters = { ...prev };
                                          delete newFilters[col];
                                          return newFilters;
                                      });
                                  }
                                : undefined
                        }
                        color={
                            selectedColumns.includes(col)
                                ? "primary"
                                : "default"
                        }
                        variant={
                            selectedColumns.includes(col)
                                ? "filled"
                                : "outlined"
                        }
                        deleteIcon={
                            selectedColumns.includes(col) ? (
                                <FiX size={16} />
                            ) : undefined
                        }
                    />
                ))}
            </Stack>
            <DataGridPremium
                localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                rows={data.filter((row) => {
                    return selectedColumns.every((col) => {
                        const filter = columnFilters[col];
                        if (!filter || filter === "all") return true;
                        const value = Number(row[col]);
                        if (filter === "sold") return value > 0;
                        if (filter === "notSold") return value === 0;
                        return true;
                    });
                })}
                columns={gridColumns}
                getRowId={(row) => row.client_id}
                initialState={{
                    pagination: {
                        paginationModel: { pageSize: 25 },
                    },
                    sorting: {
                        sortModel: [
                            { field: "warehouse_name", sort: "asc" },
                            { field: "sales_rep_name", sort: "asc" },
                            { field: "city", sort: "asc" },
                        ],
                    },
                }}
                pageSizeOptions={[25, 50, 100]}
                disableRowSelectionOnClick
                getRowHeight={() => "auto"}
                showToolbar
                sx={{
                    "& .MuiDataGrid-cell": {
                        borderColor: "rgba(224, 224, 224, 1)",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: "white",
                    },
                }}
            />
        </Box>
    );
};

export default RevenueTable;
